import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, Filter, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import Skeleton from './Skeleton';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  pagination?: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
  };
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  searchable?: boolean;
  onSearch?: (search: string) => void;
  searchPlaceholder?: string;
  filters?: React.ReactNode;
  actions?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  selectable?: boolean;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  getRowId?: (row: T) => string | number;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  pagination,
  onPageChange,
  onPerPageChange,
  onSort,
  sortBy,
  sortOrder,
  searchable = true,
  onSearch,
  searchPlaceholder = 'Search...',
  filters,
  actions,
  emptyMessage = 'No data available',
  selectable = false,
  onSelectionChange,
  getRowId = (row) => row.id,
}: DataTableProps<T>) {
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (onSearch) {
      const timer = setTimeout(() => {
        onSearch(search);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [search, onSearch]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Array.from(selectedRows));
    }
  }, [selectedRows, onSelectionChange]);

  const handleSort = (columnKey: string) => {
    if (!onSort) return;
    const column = columns.find((col) => col.key === columnKey);
    if (!column?.sortable) return;

    if (sortBy === columnKey) {
      onSort(columnKey, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(columnKey, 'asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = data.map(getRowId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (rowId: string | number, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRows(newSelected);
  };

  const allSelected = useMemo(() => {
    return data.length > 0 && data.every((row) => selectedRows.has(getRowId(row)));
  }, [data, selectedRows, getRowId]);

  const someSelected = useMemo(() => {
    return data.some((row) => selectedRows.has(getRowId(row))) && !allSelected;
  }, [data, selectedRows, allSelected, getRowId]);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const getSortIcon = (columnKey: string) => {
    if (sortBy !== columnKey) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-blue-600" />
    );
  };

  return (
    <div className="theme-card rounded-lg shadow-sm border theme-border">
      {/* Search and Filters */}
      {(searchable || filters) && (
        <div className="px-4 py-3 border-b theme-border flex flex-wrap gap-2 items-center theme-bg-tertiary">
          {searchable && (
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-tertiary w-4 h-4" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-4 py-1 theme-border rounded-lg border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary  border border-white theme-bg-primary theme-text-primary"
                />
              </div>
            </div>
          )}
          {filters && <div className="flex gap-2 flex-wrap">{filters}</div>}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="monday-table">
          <thead>
            <tr>
              {selectable && (
                <th className="w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                    ref={selectAllCheckboxRef}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.header}</span>
                    {column.sortable && onSort && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
              {actions && <th className="text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={rowIndex}>
                  {selectable && (
                    <td className="w-12">
                      <Skeleton variant="rectangular" width={16} height={16} className="mx-auto rounded" />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={column.key} style={{ width: column.width }}>
                      <Skeleton variant="text" width="80%" height={16} />
                    </td>
                  ))}
                  {actions && (
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="circular" width={24} height={24} />
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)} className="text-center py-12 text-sm text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const rowId = getRowId(row);
                return (
                  <tr key={rowId}>
                    {selectable && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(rowId)}
                          onChange={(e) => handleSelectRow(rowId, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.key}>
                        {column.render ? column.render(row) : row[column.key]}
                      </td>
                    ))}
                    {actions && (
                      <td className="text-right">
                        {actions(row)}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && onPageChange && (
        <div className="px-4 py-3 border-t theme-border flex items-center justify-between theme-bg-tertiary">
          <div className="flex items-center gap-3">
            <span className="text-sm theme-text-secondary">
              Showing {((pagination.currentPage - 1) * pagination.perPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of {pagination.total} results
            </span>
            {onPerPageChange && (
              <select
                value={pagination.perPage}
                onChange={(e) => onPerPageChange(Number(e.target.value))}
                className="border theme-border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-card theme-text-primary"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(1)}
              disabled={pagination.currentPage === 1}
              className="p-2 rounded-lg border theme-border disabled:opacity-50 disabled:cursor-not-allowed hover:theme-bg-tertiary transition-colors"
            >
              <ChevronsLeft className="w-4 h-4 theme-text-secondary" />
            </button>
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="p-2 rounded-lg border theme-border disabled:opacity-50 disabled:cursor-not-allowed hover:theme-bg-tertiary transition-colors"
            >
              <ChevronLeft className="w-4 h-4 theme-text-secondary" />
            </button>
            <span className="px-4 py-2 text-sm theme-text-secondary">
              Page {pagination.currentPage} of {pagination.lastPage}
            </span>
            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.lastPage}
              className="p-2 rounded-lg border theme-border disabled:opacity-50 disabled:cursor-not-allowed hover:theme-bg-tertiary transition-colors"
            >
              <ChevronRight className="w-4 h-4 theme-text-secondary" />
            </button>
            <button
              onClick={() => onPageChange(pagination.lastPage)}
              disabled={pagination.currentPage === pagination.lastPage}
              className="p-2 rounded-lg border theme-border disabled:opacity-50 disabled:cursor-not-allowed hover:theme-bg-tertiary transition-colors"
            >
              <ChevronsRight className="w-4 h-4 theme-text-secondary" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

